import { useToast } from './useToast'

export interface ApiConfig {
  baseURL: string
  tokenKey?: string
  defaultHeaders?: Record<string, string>
  onUnauthorized?: () => void
  onError?: (error: any) => void
}

export function useApiClient(config: ApiConfig) {
  const { showToast } = useToast()

  const getDefaultHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...config.defaultHeaders
    }

    if (config.tokenKey) {
      const token = localStorage.getItem(config.tokenKey)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    return headers
  }

  const request = async (endpoint: string, options: any = {}) => {
    try {
      const requestConfig = {
        method: options.method || 'GET',
        headers: {
          ...getDefaultHeaders(),
          ...options.headers
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
        ...options
      }

      const response = await $fetch(`${config.baseURL}${endpoint}`, requestConfig)
      return response
    } catch (error: any) {

    
      if ((error.status === 401 || error.status === 400) && error.data?.error === 'token_invalid') {
        
        if (config.tokenKey) {
          localStorage.removeItem(config.tokenKey)
        }

        // Não fazer redirect automático para a API
        // Apenas chamar o callback de unauthorized
        config.onUnauthorized?.()
      } else if (error.status === 401) {
        showToast('Sessão expirada. Faça login novamente.', 'warning')
        if (config.tokenKey) {
          localStorage.removeItem(config.tokenKey)
        }
        config.onUnauthorized?.()
      } else if (error.status === 403) {
        showToast('Acesso negado para este recurso.', 'danger')
      } else if (error.status >= 500) {
        showToast('Erro interno do servidor.', 'danger')
      } else {
        const errorMessage = error.data?.message || error.data?.error || 'Erro na requisição.'
        showToast(errorMessage, 'danger')
      }

      config.onError?.(error)
      throw error
    }
  }

  const get = (endpoint: string, options = {}) => 
    request(endpoint, { ...options, method: 'GET' })

  const post = (endpoint: string, data: any, options = {}) => 
    request(endpoint, { ...options, method: 'POST', body: data })

  const put = (endpoint: string, data: any, options = {}) => 
    request(endpoint, { ...options, method: 'PUT', body: data })

  const del = (endpoint: string, options = {}) => 
    request(endpoint, { ...options, method: 'DELETE' })

  const patch = (endpoint: string, data: any, options = {}) => 
    request(endpoint, { ...options, method: 'PATCH', body: data })

  return {
    request,
    get,
    post,
    put,
    patch,
    delete: del
  }
} 